// ============================================================
// SideQuests - Global Footer ("SideQuests © 2026")
// Premium dark-glass product footer with a compact How It Works
// strip. All surfaces use the dark-glass tokens - never white
// boxes (this is the fix for the white-on-white failure).
// Responsive, no overlap with the map/modals.
// ============================================================

import React from 'react';
import { Link } from 'react-router-dom';
import { getSocialLinks } from '../data/socialLinks';
import { HOW_IT_WORKS_STEPS } from '../data/howItWorks';
import Glyph from './Glyphs';

interface FooterProps {
  compact?: boolean;
}

export function SocialGlyph({ platform }: { platform: string }) {
  switch (platform) {
    case 'LinkedIn':
      return <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.36V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z"/></svg>;
    case 'Facebook':
      return <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.09 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.7 4.53-4.7 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.09 24 18.1 24 12.07Z"/></svg>;
    case 'TikTok':
      return <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.3 0 .58.04.86.13V9.4a6.33 6.33 0 0 0-1-.08 6.34 6.34 0 1 0 6.34 6.34V8.87a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.3Z"/></svg>;
    case 'YouTube':
      return <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M23.5 6.19a3.02 3.02 0 0 0-2.12-2.14C19.5 3.55 12 3.55 12 3.55s-7.5 0-9.38.5A3.02 3.02 0 0 0 .5 6.19C0 8.07 0 12 0 12s0 3.93.5 5.81a3.02 3.02 0 0 0 2.12 2.14c1.88.5 9.38.5 9.38.5s7.5 0 9.38-.5a3.02 3.02 0 0 0 2.12-2.14C24 15.93 24 12 24 12s0-3.93-.5-5.81ZM9.55 15.57V8.43L15.82 12l-6.27 3.57Z"/></svg>;
    case 'Nextdoor':
      return <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M10.02 17.14a1.58 1.58 0 0 1-1.65 1.5 1.73 1.73 0 0 1-1.7-1.66 1.58 1.58 0 0 1 1.66-1.5 1.72 1.72 0 0 1 1.69 1.66ZM18.99 8.55a2.2 2.2 0 0 1-1.12-.27 2.2 2.2 0 0 1-1.2-2.25 2.2 2.2 0 0 1 2.3-1.93 2.2 2.2 0 0 1 1.2 4.06 2.2 2.2 0 0 1-1.18.39ZM20 12.5c0 3.53-2.58 7.16-6.2 7.5-1.4.13-2.75-.46-3.59-1.53-.32 1.06-1.27 1.84-2.4 1.84A2.2 2.2 0 0 1 5.6 18.1c0-.06 0-.12.01-.18a3.44 3.44 0 0 1-1.98-2.86c-.05-3.3 2.35-7.35 6.05-7.98a4.4 4.4 0 0 1 1.1-.14c.6 0 1.2.13 1.75.39v-.24C12.55 5.4 14.35 4 16.05 4c.24 0 .48.02.72.05A7.98 7.98 0 0 1 20 12.5Z"/></svg>;
    case 'Reddit':
      return <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2Zm4.6 8.03c.62 0 1.12.5 1.12 1.12 0 .62-.5 1.12-1.12 1.12-.62 0-1.12-.5-1.12-1.12 0-.62.5-1.12 1.12-1.12Zm-4.1-2.43 1.2-3.9c.05-.14.2-.22.34-.18l2.8.72c.34-.4.84-.65 1.4-.65.98 0 1.78.8 1.78 1.78 0 .85-.6 1.56-1.4 1.73v2.4A4.78 4.78 0 0 1 12 14.4 4.78 4.78 0 0 1 7.38 9.9v-1.3c-.6-.25-1.05-.83-1.05-1.52 0-.9.73-1.63 1.63-1.63.63 0 1.17.35 1.45.87l2.35-.6c.13-.04.27.04.3.17l1.44 4.7h.58Zm-7.1 2.35c0-.62.5-1.12 1.12-1.12.62 0 1.12.5 1.12 1.12 0 .62-.5 1.12-1.12 1.12-.62 0-1.12-.5-1.12-1.12Zm3.6 5.03c.42.2 1.4.42 3 .42s2.58-.22 3-.42c.2-.1.44.02.52.22.08.22-.02.46-.22.55-.55.26-1.7.55-3.3.55s-2.75-.3-3.3-.55c-.2-.1-.3-.33-.22-.55.08-.2.32-.32.52-.22Z"/></svg>;
    default:
      return null;
  }
}

// The Footer renders the SAME four-step model as Home and the How It Works
// page - imported from data/howItWorks.ts, never re-declared, so the three
// surfaces can never drift apart.
export default function Footer({ compact }: FooterProps) {
  const year = new Date().getFullYear();
  // Payments and Payouts are application routes that only lead to
  // authentication, so they are deliberately NOT in the sitewide footer.
  const links = [
    { label: 'How It Works', path: '/how-it-works' },
    { label: 'SideQuests in ChatGPT', path: '/gpt' },
    { label: 'About', path: '/about' },
    { label: 'Featured Businesses', path: '/businesses' },
    { label: 'Trust & Safety', path: '/trust-and-safety' },
    { label: 'Prohibited Quests', path: '/prohibited-quests' },
    { label: 'Community', path: '/community' },
    { label: 'Leaderboard', path: '/leaderboard' },
    { label: 'FAQ', path: '/faq' },
    { label: 'Support', path: '/support' },
    { label: 'Privacy', path: '/privacy' },
    { label: 'Terms', path: '/terms' },
  ];

  return (
    <footer className="app-footer" aria-label="Footer">
      <div className="footer-inner">
        {!compact && (
          <>
            {/* How It Works - premium dark-glass strip (no white cards) */}
            <section className="footer-how" aria-label="How it works">
              <div className="footer-how-head">
                <h2 className="footer-how-title">How It Works</h2>
                <Link to="/how-it-works" className="footer-how-link">
                  Full guide <span aria-hidden="true">→</span>
                </Link>
              </div>
              <div className="footer-how-grid">
                {HOW_IT_WORKS_STEPS.map((step) => (
                  <div className="footer-how-card" key={step.number}>
                    <div className="footer-how-body">
                      <h3 className="footer-how-card-title">
                        <span className="footer-how-num" aria-hidden="true">{step.number}</span>
                        {step.short}
                      </h3>
                      <p className="footer-how-desc">{step.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <nav className="footer-links" aria-label="Footer links">
              {links.map((link) => (
                <Link key={link.path} to={link.path} className="footer-link">
                  {link.label}
                </Link>
              ))}
            </nav>
          </>
        )}

        <div className="footer-social" aria-label="Follow SideQuests">
          {getSocialLinks().map((s) => (
            <a
              key={s.platform}
              className="footer-social-link"
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`SideQuests on ${s.platform}`}
            >
              <SocialGlyph platform={s.platform} />
              <span>{s.platform}</span>
            </a>
          ))}
        </div>

        <div className="footer-brand">
          <span className="footer-mark"><Glyph name="compass" size={20} /></span>
          <span className="footer-name">SideQuests</span>
          <span className="footer-copy">© {year}</span>
        </div>
        <p className="footer-tagline">
          Find quests. Get paid. Level up. Secure payments with Stripe.
        </p>
      </div>
    </footer>
  );
}
